import React from 'react';

import { formatLinkBlockToUrl } from '$utilities/format.js';
import { IconArrowRight } from '$partials/icon/index.js';
import { SimpleImage } from '$partials/media/index.js';
import { useCarouselSwipe } from '$hooks/carousel.js';
import { LightboxItem } from '$partials/base/index.js';
import { Media } from '$partials/media/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').Carousel}
 */
const Carousel = ({
    backgroundColour,
    heading,
    link,
    modulePaddingClasses = '',
    slides,
    text
}) => {
    const { trackRef } = useCarouselSwipe();

    const href = formatLinkBlockToUrl(link);

    return (
        <section className='module-carousel'>
            <div className={`layout-container ${modulePaddingClasses}${backgroundColour ? ' bg-' + backgroundColour : ''}`}>
                <div className='layout-grid pb-16 sm:pb-32'>
                    <div className='w-10/12 sm:w-6/12 layout-grid-col'>
                        {heading && (
                            <h2 className='type-style-heading'>
                                {href ? (
                                    <a
                                        className='underline'
                                        href={href}>
                                        <span>{heading}</span>
                                    </a>
                                ) : (
                                    heading
                                )}
                            </h2>
                        )}

                        {text && (
                            <div
                                className='rich-text type-style-heading sm:w-[90%]'
                                dangerouslySetInnerHTML={{ __html: text }} />
                        )}
                    </div>
                </div>

                {slides?.length > 0 && (
                    <div className='overflow-hidden -mx-8 sm:-mx-24'>
                        <ul
                            aria-label={heading || 'Carousel'}
                            className='cursor-grab active:cursor-grabbing [&::-webkit-scrollbar]:hidden'
                            ref={trackRef}
                            tabIndex={0}>
                            {slides.map((slide) => {
                                const href = formatLinkBlockToUrl(slide.link);

                                return (
                                    <li
                                        data-carousel-slide
                                        className={`${href ? 'expand-interaction' : ''} pl-8 sm:pl-24`}
                                        key={slide.id}>
                                        {slide.image && (
                                            <figure className={`${slide?.lightbox ? '' : 'pointer-events-none '}aspect-[4/5] h-[370px] sm:h-[644px] select-none text-[0] leading-[0] tracking-normal`}>
                                                {slide?.lightbox && !href ? (
                                                    <LightboxItem
                                                        heading={slide.heading}
                                                        id={slide.id}
                                                        media={{ image: slide.image }}
                                                        text={slide.text}
                                                        wrapperClassName='h-full'>
                                                        <div className='media-wrapper text-[0] leading-[0] tracking-normal pointer-events-none select-none h-full'>
                                                            <Media
                                                                image={{
                                                                    ...slide.image,
                                                                    className: '!h-full !w-full !object-cover',
                                                                    containerClassName: '!h-full',
                                                                    containerStyle: { paddingBottom: 0 }
                                                                }}/>
                                                        </div>
                                                    </LightboxItem>
                                                ) : (
                                                    <SimpleImage
                                                        {...slide.image}
                                                        className='h-full w-full object-cover object-center'/>
                                                )}

                                            </figure>
                                        )}

                                        <div className='mt-10 sm:mt-24'>
                                            {slide.heading && (
                                                <h3 className='type-style-body font-medium'>
                                                    {href ? (
                                                        <a
                                                            className='flex items-center type-style-body-bold expand-interaction__action'
                                                            draggable={false}
                                                            href={href}>
                                                            <span>{slide.heading}</span>

                                                            <IconArrowRight
                                                                height={20}
                                                                width={20} />
                                                        </a>
                                                    ) : (
                                                        slide.heading
                                                    )}
                                                </h3>
                                            )}

                                            {slide.text && (
                                                <p className='type-style-body'>{slide.text}</p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Carousel;
